import { useEffect, useState } from "react";
import axios from "axios";
import { BASELINE } from "../../util";
import { FaRegClock, FaRegLightbulb, FaListOl, FaChartBar, FaRobot } from "react-icons/fa";

// one request per test id even if the component mounts twice (React StrictMode)
const reportRequests = {};
const fetchReport = (testId) => {
  if (!reportRequests[testId]) {
    reportRequests[testId] = axios
      .post(BASELINE + "testreport", { test_id: testId })
      .then(({ data }) => data.report)
      .catch((err) => {
        delete reportRequests[testId]; // allow retry on next mount
        throw err;
      });
  }
  return reportRequests[testId];
};

const DIFF_ORDER = ["Easy", "Medium", "Hard", "Unrated"];
const DIFF_COLORS = {
  Easy: "#34d399",
  Medium: "#fbbf24",
  Hard: "#f87171",
  Unrated: "#94a3b8",
};

const SectionCard = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center">
        <Icon className="text-indigo-500 text-xs" />
      </div>
      <p className="text-sm font-bold text-slate-700">{title}</p>
    </div>
    {children}
  </div>
);

const AIReport = ({ testData }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const testId = testData?.test_id;
    if (!testId) return;

    let alive = true;
    setLoading(true);
    setError("");
    fetchReport(testId)
      .then((r) => { if (alive) setReport(r); })
      .catch(() => { if (alive) setError("The AI report could not be generated. Please refresh to try again."); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [testData?.test_id]);

  if (!testData?.test_id) return null;

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm flex flex-col items-center justify-center text-center min-h-[320px]">
        <div className="w-10 h-10 rounded-full border-2 border-indigo-200 border-t-indigo-500 animate-spin mb-4" />
        <p className="text-sm font-bold text-slate-700 mb-1">Building your AI study report…</p>
        <p className="text-xs text-slate-400 max-w-[240px]">
          This happens only the first time you open this report — after that it loads instantly.
        </p>
      </div>
    );
  }

  if (error || !report?.ai) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center">
        <p className="text-sm font-semibold text-slate-600 mb-1">AI Report Unavailable</p>
        <p className="text-xs text-slate-400">{error || "No report data was returned."}</p>
      </div>
    );
  }

  const { stats, ai } = report;
  const fmtTime = (sec) => {
    if (sec == null) return "—";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const difficulties = DIFF_ORDER.filter((d) => stats?.by_difficulty?.[d]?.total > 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm overflow-hidden relative">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-violet-500" />
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
            <FaRobot className="text-white text-sm" />
          </div>
          <div>
            <p className="text-sm font-black text-slate-800 leading-tight">AI Study Report</p>
            <p className="text-[10px] text-slate-400">Personalized analysis of this test</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{ai.overview}</p>
      </div>

      {/* Pacing */}
      <SectionCard icon={FaRegClock} title="Pacing & Time">
        <div className="flex gap-3 mb-3">
          <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
            <p className="text-xl font-black text-slate-800 tabular-nums">{fmtTime(stats?.avg_seconds_per_question)}</p>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">avg per question</p>
          </div>
          <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
            <p className="text-xl font-black text-slate-800 tabular-nums">{fmtTime(stats?.total_seconds)}</p>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">total time</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{ai.time_analysis}</p>
      </SectionCard>

      {/* Difficulty breakdown */}
      <SectionCard icon={FaChartBar} title="Accuracy by Difficulty">
        {difficulties.length > 0 && (
          <div className="space-y-2.5 mb-3">
            {difficulties.map((d) => {
              const { correct, total } = stats.by_difficulty[d];
              const pct = Math.round((correct / total) * 100);
              return (
                <div key={d}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold text-slate-600">{d}</span>
                    <span className="text-slate-400 tabular-nums">
                      {correct}/{total} correct · <span className="font-bold text-slate-600">{pct}%</span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, background: DIFF_COLORS[d] }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-sm text-slate-600 leading-relaxed">{ai.difficulty_analysis}</p>
      </SectionCard>

      {/* Topic tips */}
      {Array.isArray(ai.topic_tips) && ai.topic_tips.length > 0 && (
        <SectionCard icon={FaRegLightbulb} title="Study Tips by Topic">
          <div className="space-y-3">
            {ai.topic_tips.map((t, i) => {
              const topicStat = stats?.by_topic?.[t.topic];
              return (
                <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs font-bold text-slate-700">{t.topic}</p>
                    {topicStat && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-500 flex-shrink-0 tabular-nums">
                        {topicStat.correct}/{topicStat.total} correct
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{t.tip}</p>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Action plan */}
      {Array.isArray(ai.action_plan) && ai.action_plan.length > 0 && (
        <SectionCard icon={FaListOl} title="How to Raise Your Score">
          <ol className="space-y-2.5">
            {ai.action_plan.map((step, i) => (
              <li key={i} className="flex gap-3 items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-[11px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-slate-600 leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </SectionCard>
      )}
    </div>
  );
};

export default AIReport;
